// src/components/resources/tools/generators/SubtitleOverlay/SubtitleOverlayOutput.tsx
'use client'

import { useCallback } from 'react'
import { toast } from 'sonner'
import { safeString } from '~/lib/utils/formatters'
import { clientLogger, logClientError } from '~/lib/logger-client'
import { SubtitleOverlayPreview } from './SubtitleOverlayPreview'
import { generateHTML } from './html-export'
import { processTemplate } from './template-processor'

/**
 * Output component that handles the final generated overlay display and download functionality
 */
export function SubtitleOverlayOutput({ formData }: { formData: Record<string, unknown> }): JSX.Element {
  // processTemplate is currently unused, but kept for potential future use.
  void processTemplate

  /**
   * Downloads the generated HTML file
   */
  const downloadHTML = useCallback((): void => {
    try {
      const html = generateHTML(formData)
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `subtitle-overlay-${new Date().toISOString().split('T')[0]}.html`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      // Show success toast
      toast.success('Download started', {
        description: 'Your subtitle overlay HTML file is being downloaded.'
      })
    } catch (error) {
      logClientError(error, { component: 'SubtitleOverlayGenerator', context: 'Failed to download HTML' })
      toast.error('Download failed', {
        description: 'An error occurred while downloading the HTML file. Please try again.'
      })
    }
  }, [formData])

  /**
   * Downloads the preview as a PNG image using html2canvas
   */
  const downloadPNG = useCallback(async (): Promise<void> => {
    try {
      const html2canvas = (await import('html2canvas')).default

      // Wait for the element to be available
      let element = document.getElementById('subtitle-overlay-final-preview')
      if (!element) {
        // Wait a bit for the element to be rendered
        await new Promise(resolve => setTimeout(resolve, 100))
        element = document.getElementById('subtitle-overlay-final-preview')
      }

      if (element) {
        // Wait for any pending renders to complete
        await new Promise(resolve => setTimeout(resolve, 200))

        const canvas = await html2canvas(element, {
          backgroundColor: null, // Transparent background
          scale: 2,
          useCORS: true,
          allowTaint: true,
          foreignObjectRendering: false,
        })

        // Check if canvas has content
        if (canvas.width === 0 || canvas.height === 0) {
          clientLogger.error('Canvas is empty', { component: 'SubtitleOverlayGenerator' })
          toast.error('Failed to generate image', {
            description: 'The preview could not be captured. Please try again.'
          })
          return
        }

        // Convert to blob and download
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `subtitle-overlay-mockup-${new Date().toISOString().split('T')[0]}.png`
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)

            // Show success toast
            toast.success('Download started', {
              description: 'Your subtitle overlay image is being downloaded.'
            })
          } else {
            throw new Error('Failed to create blob from canvas')
          }
        }, 'image/png', 1.0)
      } else {
        clientLogger.error('Preview element not found', { component: 'SubtitleOverlayGenerator' })
        toast.error('Preview not available', {
          description: 'Please ensure the preview is visible before downloading.'
        })
      }
    } catch (error) {
      logClientError(error, { component: 'SubtitleOverlayGenerator', context: 'Failed to generate PNG' })
      toast.error('Failed to generate PNG', {
        description: 'An error occurred while creating the image. Please try again.'
      })
    }
  }, [])

  // Extract form data values for display with proper trimming
  const rawSubtitleText = safeString(formData.subtitleText) || 'Enter your subtitle text here...'
  const subtitleText = rawSubtitleText.trim().replace(/\s+/g, ' ')
  const fontSize = Number(formData.fontSize) || 18
  const fontFamily = safeString(formData.fontFamily) || 'Arial, sans-serif'
  const position = safeString(formData.position) || 'Bottom Center'
  const textColor = safeString(formData.textColor) || '#ffffff'
  const backgroundColor = safeString(formData.backgroundColor) || '#000000'

  return (
    <div className="space-y-6">
      {/* Final output preview container with fixed dimensions for consistent export */}
      <div className="w-full flex justify-center">
        <div
          id="subtitle-overlay-final-preview"
          className="bg-white rounded-lg shadow-lg overflow-hidden"
          style={{
            width: '800px',
            height: '450px',
            maxWidth: '100%',
            aspectRatio: '16/9'
          }}
        >
          <div className="w-full h-full">
            <SubtitleOverlayPreview formData={formData} />
          </div>
        </div>
      </div>

      {/* Download buttons */}
      <div className="flex gap-4 justify-center">
        <button
          onClick={downloadHTML}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
        >
          Download HTML
        </button>
        <button
          onClick={downloadPNG}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
        >
          Download PNG
        </button>
      </div>

      {/* Usage instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">How to Use Your Subtitle Overlay</h4>
        <div className="text-sm text-blue-800 dark:text-blue-200 space-y-2">
          <p><strong>HTML File:</strong> Open in any web browser or import into video editing software that supports HTML overlays.</p>
          <p><strong>PNG File:</strong> Use as a static image overlay in video editing software like Adobe Premiere, Final Cut Pro, or DaVinci Resolve.</p>
          <p><strong>Customization:</strong> Edit the HTML file to adjust colors, fonts, or positioning for different video projects.</p>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-3">Design Specifications</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Text:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{subtitleText}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Font:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{fontFamily}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Size:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{fontSize}px</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Position:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{position}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Text Color:</span>
            <span
              className="ml-2 inline-block w-4 h-4 rounded border"
              style={{ backgroundColor: textColor }}
            ></span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Background:</span>
            <span
              className="ml-2 inline-block w-4 h-4 rounded border"
              style={{ backgroundColor: backgroundColor }}
            ></span>
          </div>
        </div>
      </div>
    </div>
  )
}
